import type { Shape } from "@gettactic/map-builder/src/types";
import type { Rect, ShapeById } from "../types";
import { getShapeAabb, rectsIntersect } from "./bounds";

const CELL_SIZE = 500;

/**
 * Grid-bucketed bounding boxes. Rebuilt whenever the shapes map changes
 * identity (every commit) and queried during gestures for culling and
 * snap candidates, so neither has to scan the whole document.
 */
export class SpatialIndex {
  private readonly cells = new Map<string, string[]>();
  private readonly boxes = new Map<string, Rect>();

  constructor(shapes: ShapeById, order: string[]) {
    for (const id of order) {
      const shape = shapes[id];
      if (!shape || shape.hidden) continue;
      const box = getShapeAabb(shape);
      this.boxes.set(id, box);
      for (const key of cellKeys(box)) {
        const bucket = this.cells.get(key);
        if (bucket) bucket.push(id);
        else this.cells.set(key, [id]);
      }
    }
  }

  boundsOf(id: string): Rect | undefined {
    return this.boxes.get(id);
  }

  /** Ids whose bounds intersect `area`, in no particular order. */
  query(area: Rect): string[] {
    const seen = new Set<string>();
    for (const key of cellKeys(area)) {
      const bucket = this.cells.get(key);
      if (!bucket) continue;
      for (const id of bucket) {
        if (seen.has(id)) continue;
        const box = this.boxes.get(id);
        if (box && rectsIntersect(box, area)) seen.add(id);
      }
    }
    return [...seen];
  }

  /** Shapes intersecting `area`, resolved against a shapes map. */
  shapesIn(area: Rect, shapes: ShapeById): Shape[] {
    const result: Shape[] = [];
    for (const id of this.query(area)) {
      const shape = shapes[id];
      if (shape) result.push(shape);
    }
    return result;
  }
}

const cellKeys = (rect: Rect): string[] => {
  const keys: string[] = [];
  const minCol = Math.floor(rect.x / CELL_SIZE);
  const maxCol = Math.floor((rect.x + rect.width) / CELL_SIZE);
  const minRow = Math.floor(rect.y / CELL_SIZE);
  const maxRow = Math.floor((rect.y + rect.height) / CELL_SIZE);
  for (let col = minCol; col <= maxCol; col++) {
    for (let row = minRow; row <= maxRow; row++) {
      keys.push(`${col}:${row}`);
    }
  }
  return keys;
};
